import * as React from "react";
import Box from "@mui/material/Box";
import { Button, Paper, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import moment from "moment-timezone";
import Savings from "../../components/Savings";
import CoGenSavings from "../Cogen/components/CoGenSavings";
import CostChart from "../../components/CostChart";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import { useTranslation } from "react-i18next";

import DateRangeSelector from "../../components/DateRangeSelector";
import CostBreakdown from "../../components/CostBreakdown";
import AlertSnackbar from "../../components/AlertSnackbar";
import RealTimeDataVisualizer from "../../components/RealTimeDataVisualizer";
import RecentEventList from "../../components/RecentEventList";
import { ApiContext } from "../../App";

export default function Dashboard(props) {
	const { facility, facilityInfo } = props;

	const { t } = useTranslation();

	const localTimezone = props.clientInfo.timezone;
	const currencySymbol = props.clientInfo.currency_symbol;
	const carbonIntensity = facilityInfo.carbon_intensity_g_kWh;

	var oneYearAgo = new Date();
	oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
	const [fromDate, setFromDate] = React.useState(oneYearAgo);
	const [toDate, setToDate] = React.useState(new Date());
	const [costs, setCosts] = React.useState({});
	const [costsPerPeriod, setCostsPerPeriod] = React.useState({});
	const [aggType, setAggType] = React.useState("d");
	const [costBreakdownType, setCostBreakdownType] = React.useState("location");
	const [costBySystem, setCostBySystem] = React.useState({});
	const [costByLocation, setCostByLocation] = React.useState({});
	const [events, setEvents] = React.useState([]);
	const [timeRetrievedRealTime, setTimeRetrievedRealTime] = React.useState(
		new Date()
	);
	const [realTimeData, setRealTimeData] = React.useState({});
	const [alertOpen, setAlertOpen] = React.useState(false);
	const [alertMessage, setAlertMessage] = React.useState("");
	const [alertSnackbarOpen, setAlertSnackbarOpen] = React.useState(false);
	const [alertSnackbarText, setAlertSnackbarText] = React.useState("");

	React.useEffect(() => {
		let interval = setInterval(() => {
			setTimeRetrievedRealTime(new Date());
		}, 1200000);
		return () => {
			clearInterval(interval);
		};
	}, []);

	const handleAPIError = () => {
		setAlertMessage(t("Problem fetching data."));
		setAlertOpen(true);
	};

	const {planApi, eventsApi, alarmsApi} = React.useContext(ApiContext)

	React.useEffect(() => {
		planApi.getReport(
			facility,
			fromDate,
			toDate,
			(data) => {
				setCostsPerPeriod(data);
			},
			() => {
				setCostsPerPeriod({});
				handleAPIError();
			},
			aggType
		);
	}, [fromDate, toDate, aggType, facility]);

	React.useEffect(() => {
		planApi.getReport(
			facility,
			fromDate,
			toDate,
			(data) => {
				setCosts(data);
			},
			() => {
				setCosts({});
				handleAPIError();
			}
		);

		planApi.getReport(
			facility,
			fromDate,
			toDate,
			(data) => {
				setCostBySystem(data);
			},
			() => {
				setCostBySystem({});
				handleAPIError();
			},
			"system"
		);

		planApi.getReport(
			facility,
			fromDate,
			toDate,
			(data) => {
				setCostByLocation(data);
			},
			() => {
				setCostByLocation({});
				handleAPIError();
			},
			"location"
		);
	}, [fromDate, toDate, facility]);

	React.useEffect(() => {
		eventsApi.list(
			facility,
			new Date(),
			(data) => {
				var filteredData = data.filter(
					(item) => moment.utc(item.timestamp) <= new Date()
				);

				var sortedData = filteredData.sort((a, b) => {
					if (a.timestamp < b.timestamp) return 1;
					else return -1;
				});

				var enhancedData = sortedData.map((item) => ({
					...item,
					identifier: item.point_identifier,
					name: item.point_name,
					system: item.point_system,
					location: item.point_location,
				}));

				setEvents(enhancedData.slice(0, 10));
			},
			() => {
				setEvents([]);
				handleAPIError();
			},
			true
		);
	}, [facility]);

	React.useEffect(() => {
		planApi.getCurrent(
			facility,
			(data) => {
				setRealTimeData(data);
			},
			() => {
				setRealTimeData({});
				handleAPIError();
			}
		);
	}, [timeRetrievedRealTime, facility]);

	React.useEffect(() => {
		alarmsApi.list(
			facility,
			(data) => {
				const count = data.filter(
					(item) => !item.acknowledged && !item.archived
				).length;
				if (count > 0) {
					setAlertSnackbarText(
						count + " " + (count > 1 ? t("new alerts") : t("new alert"))
					);
					setAlertSnackbarOpen(true);
				}
			},
			() => {
				handleAPIError();
			}
		);
	}, []);

	const handleAggTypeChange = (event, newValue) => {
		setAggType(newValue);
	};

	const handleCostBreakdownTypeChange = (event, newValue) => {
		if (newValue !== null) setCostBreakdownType(newValue);
	};

	return (
		<Box
			sx={{
				backgroundColor: (theme) =>
					theme.palette.mode === "light"
						? theme.palette.grey[100]
						: theme.palette.grey[900],
				overflow: "auto",
			}}
		>
			<AlertSnackbar
				open={alertSnackbarOpen}
				onClose={() => setAlertSnackbarOpen(false)}
				text={alertSnackbarText}
			/>
			<Container sx={{ mt: 4, mb: 4 }}>
				<Grid
					container
					spacing={2}
					alignItems="stretch"
					direction="row"
				>
					<Grid
						item
						xs={12}
						lg={8}
					>
						<Stack spacing={2}>
							<Paper
								sx={{
									p: 2,
								}}
							>
								<DateRangeSelector
									fromDate={fromDate}
									toDate={toDate}
									onFromDateChange={(newDate) => setFromDate(newDate)}
									onToDateChange={(newDate) => setToDate(newDate)}
								/>
							</Paper>
							<Paper
								sx={{
									p: 2,
									height: "100%",
								}}
							>
								<Stack spacing={2}>
									<Box display="flex">
										<Typography
											flexGrow={1}
											component="h2"
											variant="h6"
											color="primary"
											gutterBottom
										>
											{t("Cost profile")}
										</Typography>
										<ToggleButtonGroup
											value={aggType}
											exclusive
											size="small"
											onChange={handleAggTypeChange}
										>
											<ToggleButton value="d">{t("D")}</ToggleButton>
											<ToggleButton value="m">{t("M")}</ToggleButton>
											<ToggleButton value="q">{t("Q")}</ToggleButton>
											<ToggleButton value="y">{t("Y")}</ToggleButton>
										</ToggleButtonGroup>
									</Box>
									<Box height={380}>
										<CostChart
											consumption={costsPerPeriod}
											currencySymbol={currencySymbol}
										/>
									</Box>
								</Stack>
							</Paper>
						</Stack>
					</Grid>
					<Grid
						item
						xs={12}
						lg={4}
					>
						<Paper
							sx={{
								p: 2,
								height: "100%",
							}}
						>
							<Box
								display="flex"
								flexDirection={"column"}
								height="100%"
							>
								{costs.optimized?.[0].cost > 0 ? (
									<>
										<Savings
											totalCost={
												costs.optimized ? costs.optimized[0].cost : undefined
											}
											totalCostBaseline={
												costs.optimized ? costs.baseline[0].cost : undefined
											}
											totalConsumption={
												costs.optimized
													? costs.optimized[0].consumption
													: undefined
											}
											totalConsumptionBaseline={
												costs.optimized
													? costs.baseline[0].consumption
													: undefined
											}
											dateString={""}
											currencySymbol={currencySymbol}
											carbonIntensity={carbonIntensity}
											title={t("Consumption savings")}
											tableSize={"large"}
											mega={true}
										/>
									</>
								) : null}
								<Typography align="center">
									{t("OnDateRange", {
										from: fromDate.toLocaleDateString(),
										to: toDate.toLocaleDateString(),
										interpolation: { escapeValue: false },
									})}
								</Typography>
							</Box>
						</Paper>
					</Grid>
					<Grid
						item
						xs={12}
						md={8}
					>
						<Paper
							sx={{
								p: 2,
								height: "100%",
							}}
						>
							<Stack spacing={2}>
								<Box display="flex">
									<Typography
										flexGrow={1}
										component="h2"
										variant="h6"
										color="primary"
										gutterBottom
									>
										{t("Cost breakdown")}
									</Typography>
									<ToggleButtonGroup
										value={costBreakdownType}
										exclusive
										size="small"
										onChange={handleCostBreakdownTypeChange}
									>
										<ToggleButton value="location">
											{t("Location")}
										</ToggleButton>
										<ToggleButton value="system">{t("System")}</ToggleButton>
									</ToggleButtonGroup>
								</Box>
								<Box height={350} sx={{overflowY: "auto", overflowX: "clip"}}>
									<CostBreakdown
										data={
											costBreakdownType === "system"
												? costBySystem.optimized
												: costByLocation.optimized
										}
										nameField={costBreakdownType}
										valueField="cost"
										currencySymbol={currencySymbol}
									/>
								</Box>
							</Stack>
						</Paper>
					</Grid>
					<Grid
						item
						xs={12}
						md={4}
					>
						<Paper
							sx={{
								p: 2,
								height: "100%",
							}}
						>
							<Box
								display="flex"
								flexDirection={"column"}
								height="100%"
							>
								<Box display="flex">
									<Typography
										flex={1}
										component="h2"
										variant="h6"
										color="primary"
										gutterBottom
									>
										{t("Real-time data")}
									</Typography>
									<Typography variant="subtitle1">
										{timeRetrievedRealTime.toLocaleTimeString([], {
											hour: "2-digit",
											minute: "2-digit",
										})}
									</Typography>
								</Box>
								<Box flex={1} />
								<RealTimeDataVisualizer
									currencySymbol={currencySymbol}
									data={realTimeData}
								/>
								<Box flex={1} />
							</Box>
						</Paper>
					</Grid>
					<Grid
						item
						xs={12}
					>
						<Paper
							sx={{
								p: 2,
								height: "100%",
							}}
						>
							<Typography
								flexGrow={1}
								component="h2"
								variant="h6"
								color="primary"
								gutterBottom
							>
								{t("Latest events")}
							</Typography>
							<Box height={300}>
								<RecentEventList
									events={events}
									localTimezone={localTimezone}
								/>
							</Box>
						</Paper>
					</Grid>
				</Grid>
			</Container>
			<Dialog
				open={alertOpen}
				onClose={() => setAlertOpen(false)}
			>
				<DialogTitle id="alert-dialog-title">{t("Problem")}</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						{alertMessage}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setAlertOpen(false)}
						autoFocus
					>
						{t("OK")}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
